import React, { useCallback, useMemo, useState } from "react"
import { useMutation } from "urql"

import { FilePicker } from "../../../../components/shared/FileStackWidgets"
import { REMOVE_PORTAL_DOCUMENT_MUTATION } from "../../../../graphql/mutations/removePortalDocument"
import { UPLOAD_PORTAL_DOCUMENT_MUTATION } from "../../../../graphql/mutations/uploadPortalDocument"
import { AnimatedModal } from "../../../../components/shared/Modal"

const FIVE_MB = 5 * 1024 * 1024

const DOCUMENT_ACCEPT = [
  "application/pdf",
  ".pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".docx",
  "text/csv",
  "application/csv",
  ".csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xlsx"
]

const tabs = [
  { id: "uploaded", label: "Uploaded documents" },
  { id: "upload", label: "Upload new" }
] as const

type TabId = (typeof tabs)[number]["id"]

export type UploadedPortalDocument = {
  id: string | number
  url: string
  filename: string
  handle: string
  contentType: string
  provider: string
}

export type DocumentPickerSelection = {
  url: string
  filename: string
  handle: string
  provider: "filestack"
  contentType: string
  kind: "document"
}

export type DocumentPickerV2Props = {
  uploadedDocuments?: UploadedPortalDocument[]
  onUploadedDocumentsChange?: (documents: UploadedPortalDocument[]) => void
  onDocumentSelect: (document: DocumentPickerSelection) => void
  fetching?: boolean
  trigger?: React.ReactNode
  uploadError?: string | null
  onUploadError?: (message: string | null) => void
}

function normalizeUploadedDoc(raw: Record<string, unknown>): UploadedPortalDocument | null {
  const id = raw.id
  const url = String(raw.url || "")
  if (!id || !url) return null
  return {
    id: id as string | number,
    url,
    filename: String(raw.filename || "document"),
    handle: String(raw.handle || ""),
    contentType: String(raw.contentType || raw.content_type || ""),
    provider: String(raw.provider || "filestack")
  }
}

function documentIcon(contentType: string) {
  const lower = contentType.toLowerCase()
  const isPdf = lower.includes("pdf")
  const isSpreadsheet = lower.includes("csv") || lower.includes("spreadsheet") || lower.includes("excel")
  const label = lower.includes("csv") ? "CSV" : lower.includes("spreadsheet") || lower.includes("sheet") ? "XLSX" : isPdf ? null : "DOCX"
  return (
    <svg className="size-8 shrink-0 text-builder-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
      {isPdf || isSpreadsheet ? null : (
        <text x="12" y="14" textAnchor="middle" className="fill-current text-[6px] font-bold">
          {label}
        </text>
      )}
    </svg>
  )
}

/**
 * DocumentPickerV2 — portal AI document library (PDF/DOCX/CSV/XLSX), upload + reuse + delete.
 */
export default function DocumentPickerV2({
  uploadedDocuments = [],
  onUploadedDocumentsChange,
  onDocumentSelect,
  fetching = false,
  trigger = null,
  uploadError = null,
  onUploadError
}: DocumentPickerV2Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | number | null>(null)
  const [registering, setRegistering] = useState(false)
  const [deletingId, setDeletingId] = useState<string | number | null>(null)
  const initialTab: TabId = uploadedDocuments.length === 0 ? "upload" : "uploaded"
  const [activeTab, setActiveTab] = useState<TabId>(initialTab)

  const [, uploadPortalDocument] = useMutation(UPLOAD_PORTAL_DOCUMENT_MUTATION)
  const [, removePortalDocument] = useMutation(REMOVE_PORTAL_DOCUMENT_MUTATION)

  const busy = fetching || registering || deletingId != null

  const selectedDocument = useMemo(
    () => uploadedDocuments.find((d) => String(d.id) === String(selectedId)) ?? null,
    [uploadedDocuments, selectedId]
  )

  const setDocuments = useCallback(
    (next: UploadedPortalDocument[]) => {
      onUploadedDocumentsChange?.(next)
    },
    [onUploadedDocumentsChange]
  )

  const handleUploadDone = async (res: {
    filesUploaded: Array<{ url: string; filename: string; handle: string; mimetype?: string; size?: number }>
  }) => {
    const file = res.filesUploaded[0]
    if (!file?.url) return

    const contentType =
      file.mimetype ||
      (file.filename?.toLowerCase().endsWith(".docx")
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf")

    onUploadError?.(null)
    setRegistering(true)
    try {
      const result = await uploadPortalDocument({
        handle: file.handle,
        url: file.url,
        filename: file.filename || "document",
        contentType,
        provider: "filestack",
        byteSize: file.size ?? undefined
      })
      const payload = result?.data?.uploadPortalDocument
      if (payload?.result !== "success" || !payload?.document) {
        const msg = payload?.errors?.join(", ") || "Could not save document."
        onUploadError?.(msg)
        return
      }

      const doc = normalizeUploadedDoc(payload.document as Record<string, unknown>)
      if (!doc) {
        onUploadError?.("Could not save document.")
        return
      }

      setDocuments([doc, ...uploadedDocuments.filter((d) => String(d.id) !== String(doc.id))])
      setIsOpen(false)
      onDocumentSelect({
        url: doc.url,
        filename: doc.filename,
        handle: doc.handle,
        provider: "filestack",
        contentType: doc.contentType,
        kind: "document"
      })
    } catch {
      onUploadError?.("Could not save document.")
    } finally {
      setRegistering(false)
    }
  }

  const handleSave = () => {
    if (!selectedDocument) return
    setIsOpen(false)
    onDocumentSelect({
      url: selectedDocument.url,
      filename: selectedDocument.filename,
      handle: selectedDocument.handle,
      provider: "filestack",
      contentType: selectedDocument.contentType,
      kind: "document"
    })
    setSelectedId(null)
  }

  const handleDelete = async (doc: UploadedPortalDocument, event: React.MouseEvent) => {
    event.stopPropagation()
    if (busy) return
    if (!window.confirm(`Remove "${doc.filename}" from your uploaded documents?`)) return

    setDeletingId(doc.id)
    onUploadError?.(null)
    try {
      const result = await removePortalDocument({ id: String(doc.id) })
      const payload = result?.data?.removePortalDocument
      if (payload?.result !== "success") {
        onUploadError?.(payload?.errors?.join(", ") || "Could not remove document.")
        return
      }
      const next = uploadedDocuments.filter((d) => String(d.id) !== String(doc.id))
      setDocuments(next)
      if (String(selectedId) === String(doc.id)) setSelectedId(null)
    } catch {
      onUploadError?.("Could not remove document.")
    } finally {
      setDeletingId(null)
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "upload":
        return (
          <div className="p-4">
            <p className="mb-4 text-left font-['Manrope:Regular',_sans-serif] text-[14px] leading-[20px] text-builder-secondary">
              Upload a PDF or DOCX (max 5 MB). Legacy .doc files are not supported.
            </p>
            {uploadError ? <p className="mb-3 text-sm text-red-600">{uploadError}</p> : null}
            <FilePicker
              as="button"
              onUploadDone={handleUploadDone}
              disabled={busy}
              maxFiles={1}
              maxSize={FIVE_MB}
              accept={DOCUMENT_ACCEPT}
              transformations={null}
              imageMin={undefined}
              fromSources={undefined}
              className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 rounded border border-builder-accent bg-white px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-accent transition-colors hover:bg-builder-bg-light disabled:cursor-not-allowed disabled:opacity-50">
              <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {registering ? "Saving…" : "Choose PDF or DOCX"}
            </FilePicker>
          </div>
        )
      case "uploaded":
        return (
          <div className="max-h-[calc(100vh-350px)] overflow-y-auto p-4">
            {uploadError ? <p className="mb-3 text-sm text-red-600">{uploadError}</p> : null}
            {uploadedDocuments.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {uploadedDocuments.map((doc) => {
                  const isSelected = String(selectedId) === String(doc.id)
                  const isDeleting = String(deletingId) === String(doc.id)
                  return (
                    <li key={String(doc.id)}>
                      <div
                        role="button"
                        tabIndex={0}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-all ${
                          isSelected
                            ? "border-builder-accent ring-2 ring-builder-accent"
                            : "border-gray-200 hover:border-builder-accent/50"
                        }`}
                        onClick={() => setSelectedId(isSelected ? null : doc.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            setSelectedId(isSelected ? null : doc.id)
                          }
                        }}>
                        {documentIcon(doc.contentType)}
                        <span className="min-w-0 flex-1 truncate font-['Manrope:Regular',_sans-serif] text-[14px] text-builder-primary">
                          {doc.filename}
                        </span>
                        <button
                          type="button"
                          className="shrink-0 rounded p-1 text-builder-secondary hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                          aria-label={`Delete ${doc.filename}`}
                          disabled={busy}
                          onClick={(e) => handleDelete(doc, e)}>
                          {isDeleting ? (
                            <span className="text-xs">…</span>
                          ) : (
                            <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="flex h-32 items-center justify-center text-center">
                <p className="text-[14px] text-builder-secondary">No documents uploaded yet.</p>
              </div>
            )}
          </div>
        )
      default:
        return null
    }
  }

  const defaultTrigger = (
    <button
      type="button"
      disabled={busy}
      className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 rounded border border-builder-accent bg-white px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-accent transition-colors hover:bg-builder-bg-light disabled:cursor-not-allowed disabled:opacity-50">
      Upload document
    </button>
  )

  return (
    <>
      {trigger ? (
        <div
          onClick={() => {
            if (!busy) {
              setActiveTab(uploadedDocuments.length === 0 ? "upload" : "uploaded")
              setIsOpen(true)
            }
          }}>
          {trigger}
        </div>
      ) : (
        defaultTrigger
      )}

      <AnimatedModal
        visible={isOpen}
        hideModal={() => {
          setIsOpen(false)
          setSelectedId(null)
          onUploadError?.(null)
        }}
        header="Document"
        showFooter={true}
        actionButtonCopy={activeTab === "upload" ? "Upload" : selectedDocument ? "Use document" : "Select document"}
        saveDisabled={activeTab !== "upload" && !selectedDocument}
        onSave={activeTab === "upload" ? () => {} : handleSave}
        dialogClassName="max-h-[100vh] flex flex-col w-[520px] md:w-full">
        <div className="flex h-full flex-col">
          <div className="relative mb-4 w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[20px] font-bold text-builder-primary">
            <p className="block leading-[28px]">Document selection</p>
          </div>

          <div className="mb-4 flex gap-6 border-b border-builder-border-light">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`relative pb-3 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold transition-colors ${
                  tab.id === activeTab
                    ? "border-b-2 border-builder-accent text-builder-accent"
                    : "text-builder-secondary hover:text-builder-primary"
                }`}
                onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grow overflow-hidden">{renderTabContent()}</div>
        </div>
      </AnimatedModal>
    </>
  )
}
